import { Component, inject, OnInit, signal, computed, effect, DestroyRef } from '@angular/core';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { SkeletonModule } from 'primeng/skeleton';
import { ToastService } from '../../../../trash/shared/components';
import { ErrorOrError, handleApiErrors } from '../../../../common';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-formly-plate-license',
  templateUrl: './formly-plate-license.component.html',
  styleUrls: ['./formly-plate-license.component.scss'],
  standalone: true,
  imports: [
    TranslateModule,
    InputNumberModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    InputTextModule,
    FormlyModule,
    SkeletonModule,
    TooltipModule
  ]
})
export class FormlyPlateLicense extends FieldType implements OnInit {
  private destroyRef = inject(DestroyRef);
  private translate = inject(TranslateService);
  toastService: ToastService = inject(ToastService);

  // Form Controls
  numberControl = new FormControl('');
  lettersControl = new FormControl('');

  // Signals
  numberValue = toSignal(this.numberControl.valueChanges, {
    initialValue: ''
  });
  lettersValue = toSignal(this.lettersControl.valueChanges, {
    initialValue: ''
  });
  isLoading = signal(true);
  countrySettings = signal<any>({});
  validator = signal<any>(null);

  // Computed values
  originalNumbers = computed(() => {
    const controlValue = this.numberControl.value;
    const signalValue = this.numberValue();

    if (controlValue !== null && controlValue !== undefined) {
      return String(controlValue);
    } else if (signalValue !== null && signalValue !== undefined) {
      return String(signalValue);
    }
    return '';
  }); // Show original input digits
  originalLetters = computed(() => (this.lettersValue() || '').split(/\s+/).filter((l) => l.trim()));
  mappedNumbers = computed(() => {
    // Handle both string and number values from p-inputNumber
    const controlValue = this.numberControl.value;
    const signalValue = this.numberValue();

    let input = '';
    if (controlValue !== null && controlValue !== undefined) {
      input = String(controlValue);
    } else if (signalValue !== null && signalValue !== undefined) {
      input = String(signalValue);
    }

    return this.convertToArabicNumbers(input);
  }); // Show Arabic digits
  mappedLetters = computed(() => this.convertToMappedLetters(this.lettersValue() || ''));

  constructor() {
    super();
    this.setupEffects();
  }

  ngOnInit() {
    this.setupFormControls();
    this.loadValidator();
    this.handleDisabledState();
  }

  private setupEffects() {
    // Update model when form values change
    effect(() => {
      this.ensureLicensePlateDataExists();
      this.model.licensePlateData.plateDigits = this.numberValue() || '';
      this.model.licensePlateData.plateLetters = this.getMappedEnglishLetters() || '';
    });

    // Update display when validator changes OR vehicle type changes
    effect(() => {
      const validator = this.validator();
      // Get vehicle type from props signal to make it reactive
      const vehicleType = this.getVehicleTypeFromProps();

      if (validator && vehicleType) {
        this.updateCountrySettings(validator);
      }
    });
  }

  // Handle letters input events
  onLettersInput(event: any) {
    const processedValue = this.processLettersInput(event.target.value);
    this.lettersControl.setValue(processedValue, { emitEvent: true });
  }

  // Process letters input to enforce uppercase and whitespace separation
  private processLettersInput(value: string): string {
    if (!value) return '';

    // Auto-separate consecutive letters and process
    const separated = value
      .split(/\s+/)
      .filter((part) => part.trim())
      .map((part) => {
        if (part.length > 1 && /^[a-zA-Z\u0600-\u06FF]+$/.test(part)) {
          return part.split('').join(' ');
        }
        return part;
      })
      .join(' ');

    // Split and process each letter
    return separated
      .split(/\s+/)
      .filter((letter) => letter.trim())
      .map((letter) => (this.isArabicLetter(letter) ? letter : letter.toUpperCase()))
      .join(' ');
  }

  // Validation helpers
  isArabicLetter(char: string): boolean {
    return /[\u0600-\u06FF]/.test(char);
  }

  hasInvalidCharacters() {
    const numbers = this.numberControl.value?.toString() || '';
    const { maxDigits, minDigits } = this.countrySettings();
    return numbers && maxDigits && (numbers.length < minDigits || numbers.length > maxDigits);
  }

  hasInvalidLetters() {
    const letters = this.lettersControl.value?.toString() || '';
    const { maxLetters, minLetters } = this.countrySettings();
    const letterCount = letters.split(/\s+/).filter((l) => l.trim()).length;

    const hasLengthError = letters && maxLetters && (letterCount < minLetters || letterCount > maxLetters);
    return hasLengthError || this.hasInvalidAllowedLetters();
  }

  hasInvalidAllowedLetters() {
    const letters = this.lettersControl.value?.toString() || '';
    if (!letters) return false;

    const mappingLetters = this.validator()?.mappingLetters || [];
    if (!mappingLetters.length) return false;

    const inputLetters = letters.split(/\s+/).filter((l) => l.trim());

    return inputLetters.some((letter) => {
      if (this.isArabicLetter(letter)) {
        return !mappingLetters.some((m: any) =>
          m.mappedLetters?.some((ml: string) => ml.toLowerCase() === letter.toLowerCase())
        );
      }
      return !mappingLetters.some((m: any) => m.letter.toLowerCase() === letter.toLowerCase());
    });
  }

  // Error messages
  errorMessageNumbers() {
    if (!this.hasInvalidCharacters()) return '';
    const { minDigits, maxDigits } = this.countrySettings();
    const t = this.translate;
    return `${t.instant('VEHICLE_FLOW.NUMBERS_MUST_BE_BETWEEN')} ${minDigits} ${t.instant('VEHICLE_FLOW.DIGITS')} ${t.instant('GENERAL.AND')} ${maxDigits} ${t.instant('VEHICLE_FLOW.DIGITS')}`;
  }

  errorMessageLetters() {
    if (!this.hasInvalidLetters()) return '';

    const letters = this.lettersControl.value?.toString() || '';
    const { minLetters, maxLetters } = this.countrySettings();
    const letterCount = letters.split(/\s+/).filter((l) => l.trim()).length;

    if (letters && maxLetters && (letterCount < minLetters || letterCount > maxLetters)) {
      const t = this.translate;
      return `${t.instant('VEHICLE_FLOW.LETTERS_MUST_BE_BETWEEN')} ${minLetters} ${t.instant('VEHICLE_FLOW.CHARACTERS')} ${t.instant('GENERAL.AND')} ${maxLetters} ${t.instant('VEHICLE_FLOW.CHARACTERS')}`;
    }

    return this.hasInvalidAllowedLetters() ? this.translate.instant('VEHICLE_FLOW.INVALID_CHARACTERS') : '';
  }

  private loadValidator() {
    const checkInterval = setInterval(() => {
      const validator = this.props['countryPlateValidator']?.();
      if (validator) {
        clearInterval(checkInterval);
        this.validator.set(validator);
        this.isLoading.set(false);
      }
    }, 100);

    setTimeout(() => {
      clearInterval(checkInterval);
      if (this.isLoading()) {
        this.setDefaultValues();
        this.isLoading.set(false);
      }
    }, 5000);
  }

  private setDefaultValues() {
    this.countrySettings.set({
      maxDigits: 4,
      minDigits: 1,
      maxLetters: 4,
      minLetters: 1,
      logo: 'assets/flags/global.svg',
      text: 'GL'
    });
  }

  private ensureLicensePlateDataExists() {
    if (!this.model.licensePlateData) {
      this.model.licensePlateData = {
        plateLetters: '',
        plateDigits: '',
        country: { id: '', englishName: '' },
        plateLicenseType: { id: '', englishName: '' }
      };
    }
  }

  private setupFormControls() {
    this.ensureLicensePlateDataExists();

    const { plateDigits = '', plateLetters = '' } = this.model.licensePlateData;
    this.numberControl.setValue(plateDigits, { emitEvent: false });
    this.lettersControl.setValue(this.processLettersInput(plateLetters), { emitEvent: false });

    // Setup value change subscriptions
    this.numberControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.lettersControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private handleDisabledState() {
    const isDisabled = this.props['disabled'] || false;
    isDisabled ? this.numberControl.disable() : this.numberControl.enable();
    isDisabled ? this.lettersControl.disable() : this.lettersControl.enable();
  }

  private updateCountrySettings(validator: any) {
    const currentVehicleType = this.getVehicleTypeFromProps();
    const vehicleValidation = validator.vehicleTypePlateValidations?.find(
      (v: any) => v.vehicleType === currentVehicleType
    );

    if (vehicleValidation) {
      const { plateValidation } = vehicleValidation;
      const { englishName } = validator.country;
      this.countrySettings.set({
        ...plateValidation,
        logo: this.getCountryLogo(englishName),
        text: this.getCountryText(englishName)
      });
    } else {
      this.setDefaultValues();
      handleApiErrors(
        [
          ErrorOrError.validation(
            this.translate.instant('GENERAL.ERROR'),
            this.translate.instant('VEHICLE_FLOW.NO_VALIDATOR_FOUND')
          )
        ],
        this.toastService
      );
    }
  }

  private getCountryText(name: string): string {
    const codes: Record<string, string> = {
      'saudi arabia': 'SA',
      egypt: 'EG'
    };
    return codes[name.toLowerCase()] || name.substring(0, 2).toUpperCase();
  }

  private getCountryLogo(name: string): string {
    const logos: Record<string, string> = {
      'saudi arabia': 'assets/flags/flag-ksa-icon.svg',
      egypt: 'assets/flags/flag-egypt-icon.svg'
    };
    return logos[name.toLowerCase()] || 'assets/flags/global.svg';
  }

  private convertToArabicNumbers(numbers: string): string {
    if (typeof numbers !== 'string') return '';

    const arabicNums = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
    return numbers.replace(/\d/g, (d) => arabicNums[+d]);
  }

  private convertToMappedLetters(letters: string): string[] {
    if (typeof letters !== 'string') return [];

    const mappingLetters = this.validator()?.mappingLetters || [];
    const letterArray = letters.split(/\s+/).filter((l) => l.trim());

    return letterArray.map((letter) => {
      if (this.isArabicLetter(letter)) {
        const mapping = mappingLetters.find((m: any) =>
          m.mappedLetters?.some((ml: string) => ml.toLowerCase() === letter.toLowerCase())
        );
        return mapping?.letter || letter;
      }

      const mapping = mappingLetters.find((m: any) => m.letter.toLowerCase() === letter.toLowerCase());
      return mapping?.mappedLetters?.[0] || letter;
    });
  }

  // Get vehicle type from props signal to make it reactive
  private getVehicleTypeFromProps(): string {
    try {
      const vehicleTypeSignal = this.props['vehicleTypeSignal'];
      if (vehicleTypeSignal && typeof vehicleTypeSignal === 'function') {
        return vehicleTypeSignal() || '';
      }
      // Fallback to model value
      return this.model?.vehicleType || '';
    } catch (error) {
      return this.model?.vehicleType || '';
    }
  }

  // Utility methods
  getNumbersValue() {
    return this.numberValue() || '';
  }

  getLettersValue() {
    return this.processLettersInput(this.lettersValue() || '');
  }

  getAllowedLetters() {
    return this.validator()?.mappingLetters?.map((m: any) => m.letter) || [];
  }

  getAllowedLettersTooltip() {
    const letters = this.getAllowedLetters();
    if (letters.length) {
      return this.translate.instant('VEHICLE_FLOW.ALLOWED_LETTERS') + ': ' + letters.join(', ');
    }
    return '';
  }
  private convertLetterCountToInputLength(letterCount: number | undefined): number | null {
    if (!letterCount || letterCount <= 0) return null;
    return 2 * letterCount - 1;
  }

  getLettersInputMinLength(): number | null {
    const { minLetters } = this.countrySettings();
    return this.convertLetterCountToInputLength(minLetters);
  }

  getLettersInputMaxLength(): number | null {
    const { maxLetters } = this.countrySettings();
    return this.convertLetterCountToInputLength(maxLetters);
  }

  // Get mapped English letters for backend submission
  getMappedEnglishLetters(): string {
    const letters = this.lettersValue() || '';
    if (!letters) return '';

    const mappingLetters = this.validator()?.mappingLetters || [];
    const letterArray = letters.split(/\s+/).filter((l) => l.trim());

    return letterArray
      .map((letter) => {
        if (this.isArabicLetter(letter)) {
          const mapping = mappingLetters.find((m: any) =>
            m.mappedLetters?.some((ml: string) => ml.toLowerCase() === letter.toLowerCase())
          );
          return mapping?.letter || letter;
        }
        return letter.toUpperCase();
      })
      .join('');
  }
}
